import { Link, useLocation } from "react-router-dom";
import { PanelLeftClose, PanelLeftOpen, PowerOff } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import logo from "../../../assets/logo.svg";
import logoMark from "../../../assets/logoMark.svg";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
} from "../../ui/sidebar.tsx";
import { cn } from "../../../lib/utils.ts";
import {
  STATIC_NAV_ITEMS,
  ORG_NAV_CHILDREN,
  ADD_ORG_NAV_ITEM,
} from "./constants.ts";
import { useOrganizationStore } from "../../../stores/useOrganizationStore.ts";
import { useOrganizations } from "../../../data/hooks/useOrganizations.ts";
import type { Organization } from "../../../data/types.ts";
import { useState } from "react";
import { ChevronDown, ChevronUp, Plus } from "lucide-react";
import { useCurrentRole } from "../../../data/hooks/useCurrentRole.ts";
import { canAccess, isPlatformAdmin } from "../../../lib/rbac.ts";

type AppSidebarProps = {
  isCollapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
function OrgAvatar({ name }: { name: string }) {
  return (
    <span className="flex items-center justify-center size-5 rounded-md text-xs font-bold shrink-0 bg-[var(--nav-org-badge-bg)] text-[var(--nav-org-badge-text)]">
      {name.charAt(0).toUpperCase()}
    </span>
  );
}

// ─── Collapsed Nav Item ───────────────────────────────────────────────────────
function CollapsedNavItem({
  path,
  icon: Icon,
  prefetch,
}: {
  label: string;
  path: string;
  icon?: LucideIcon;
  prefetch?: () => void;
}) {
  const { pathname } = useLocation();
  const isActive = pathname === path;

  return (
    <Link
      to={path}
      onMouseEnter={() => prefetch?.()}
      className={cn(
        "flex items-center justify-center size-9 rounded-md transition-colors",
        "text-[var(--nav-item-text)] hover:bg-[#eef4fe] hover:text-[var(--deep-sky-denim)]",
        isActive && "bg-[#eef4fe] text-[var(--deep-sky-denim)]",
      )}
    >
      {Icon && <Icon className="size-[15px] shrink-0" />}
    </Link>
  );
}

// ─── Expanded Nav Item ────────────────────────────────────────────────────────
function StaticNavLeaf({
  label,
  path,
  icon: Icon,
  prefetch,
}: {
  label: string;
  path: string;
  icon?: LucideIcon;
  prefetch?: () => void;
}) {
  const { pathname } = useLocation();
  const isActive = pathname === path;

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        isActive={isActive}
        className={cn(
          "flex items-center gap-3 w-full px-3 h-9 text-[13px] font-medium rounded-md transition-colors cursor-pointer",
          "text-[var(--nav-item-text)] hover:bg-[#eef4fe] hover:text-[var(--deep-sky-denim)]",
          isActive && "bg-[#eef4fe] text-[var(--deep-sky-denim)] font-medium",
        )}
      >
        <Link to={path} onMouseEnter={() => prefetch?.()} className="flex items-center gap-3 w-full">
          {Icon && <Icon className="size-[15px] shrink-0" />}
          <span>{label}</span>
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

// org.facilities is pre-filtered server-side to the facilities this user can
// open: active ones, plus — for Joules platform admins only — deactivated
// ones (view-only, JAA-1117), listed like any other facility. So for a
// platform admin an org stays listed even when every facility is
// deactivated. An empty array means there's nothing to navigate to; those
// orgs stay manageable from the Admin Dashboard, they just don't get a
// sidebar entry.
function orgHasActiveFacilities(org: Organization): boolean {
  return !!org.facilities?.length && org.facilityCount !== 0;
}

// activeFacilityId mirrors the URL, so it can hold an ID that isn't one of
// this org's facilities (a typed or stale link). Links built from it would
// keep the user on that invalid facility — fall back to the org's first
// facility instead. With no org to check against (organizations still
// loading), the active ID is kept as-is.
function resolveNavFacilityId(
  org: Organization | undefined,
  activeFacilityId: number,
): number {
  if (!org) return activeFacilityId;
  const isOrgFacility = org.facilities?.some(
    (facility) => facility.id === activeFacilityId,
  );
  return isOrgFacility ? activeFacilityId : (org.facilities?.[0]?.id ?? 0);
}

// ─── Org Nav Group ────────────────────────────────────────────────────────────
function OrgNavGroup({
  org,
  isCollapsed,
  defaultOpen = false,
}: {
  org: Organization;
  isCollapsed: boolean;
  defaultOpen?: boolean;
}) {
  const { pathname } = useLocation();
  const { activeOrgId, activeFacilityId } = useOrganizationStore();

  const hasNoFacilities = !orgHasActiveFacilities(org);
  const isExpanded = activeOrgId === org.id;

  // ── Auto-expand logic ──────────────────────────────────────────────────────
  // defaultOpen is true for org/facility users so their org is expanded on load.
  // For platform admins the chevron state drives expansion (isExpanded from store).
  // Called unconditionally, before the hasNoFacilities early return below, to
  // satisfy the Rules of Hooks — every render of this component must call the
  // same hooks in the same order regardless of which branch it then takes.
  const [isOpen, setIsOpen] = useState(isExpanded || defaultOpen);

  if (hasNoFacilities) return null;

  const facilityIdForNav = isExpanded
    ? resolveNavFacilityId(org, activeFacilityId)
    : (org.facilities?.[0]?.id ?? 0);

  const children = ORG_NAV_CHILDREN(org.id, facilityIdForNav);
  const isAnyChildActive = children.some((c) => pathname === c.path);

  function handleChevronToggle() {
    const opening = !isOpen;
    setIsOpen(opening);
  }

  // ── Collapsed — avatar only ───────────────────────────────────────────────
  if (isCollapsed) {
    return (
      <Link
        to={`/organizations/${org.id}/facilities/${facilityIdForNav}/overview`}
        className={cn(
          "flex items-center justify-center size-9 rounded-md transition-colors mx-auto",
          !facilityIdForNav
            ? "opacity-40 cursor-not-allowed pointer-events-none"
            : isAnyChildActive
              ? "bg-[#eef4fe] text-[var(--deep-sky-denim)]"
              : "hover:bg-[#eef4fe]",
        )}
      >
        <OrgAvatar name={org.name} />
      </Link>
    );
  }

  // ── Expanded — full org group with children ───────────────────────────────
  return (
    <SidebarMenuItem className="mb-0.5">
      <div className={cn("overflow-hidden rounded-md")}>
        <SidebarMenuButton
          asChild
          isActive={isAnyChildActive}
          onClick={handleChevronToggle}
          className={cn(
            "flex items-center gap-2 w-full px-3 h-9 text-[13px] font-normal rounded-md transition-colors cursor-pointer",
            isExpanded
              ? "text-[var(--deep-sky-denim)] font-medium rounded-b-none"
              : "text-[var(--nav-item-text)]",
          )}
        >
          <div className="flex items-center gap-2 w-full">
            <OrgAvatar name={org.name} />
            <span className="flex-1 truncate">{org.name}</span>
            {isOpen ? (
              <ChevronUp className="size-3 shrink-0 text-[var(--deep-sky-denim)]" />
            ) : (
              <ChevronDown className="size-3 shrink-0 text-[var(--nav-item-text-muted)]" />
            )}
          </div>
        </SidebarMenuButton>

        {isOpen && (
          <div className="flex flex-col ml-[18px] mb-1 border-l-1 border-gray-200 pl-2 gap-1">
            {children.map((child) => {
              const isSubActive = pathname === child.path;
              const SubIcon = child.icon;

              return (
                <Link
                  key={child.path}
                  to={child.path}
                  onMouseEnter={() => child.prefetch?.()}
                  className={cn(
                    "flex flex-row items-center gap-2.5 w-full pl-3 pr-2 py-1 text-[13px] font-normal transition-colors cursor-pointer rounded-md",
                    "text-[var(--nav-item-text)] hover:bg-[#eef4fe] hover:text-[var(--deep-sky-denim)]",
                    isSubActive &&
                    "bg-[#eef4fe] text-[var(--deep-sky-denim)] font-medium",
                  )}
                >
                  {SubIcon && <SubIcon className="size-[15px] shrink-0" />}
                  <span>{child.label}</span>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </SidebarMenuItem>
  );
}

// ─── AppSidebar ───────────────────────────────────────────────────────────────
export default function AppSidebar({
  isCollapsed,
  onCollapsedChange,
}: AppSidebarProps) {
  const { organizations, loading, error } = useOrganizations();
  const { activeOrgId, activeFacilityId } = useOrganizationStore();
  const role = useCurrentRole();

  const visibleNavItems = STATIC_NAV_ITEMS.filter((item) =>
    item.pageKey ? canAccess(item.pageKey, role) : true,
  );

  // Org/facility users (platform_user) should see their org expanded by default
  // so they land directly on the facility list without needing to click.
  const isPlatformUser = role === "platform_user";

  // True once orgs have loaded and at least one has an active facility to
  // list — OrgNavGroup renders null for every org that doesn't, so without
  // this the "Organizations" section can render with nothing under it (most
  // commonly a platform_user whose only facility was deactivated, landing
  // them on /facility-deactivated with an otherwise-blank sidebar).
  const hasVisibleOrgs = organizations.some(orgHasActiveFacilities);
  const showNoFacilitiesMessage =
    !loading && !error && !hasVisibleOrgs && isPlatformUser;

  const logoFacilityId = resolveNavFacilityId(
    organizations.find((org) => org.id === activeOrgId),
    activeFacilityId,
  );
  const logoHref = isPlatformAdmin(role)
    ? "/admin/dashboard"
    : activeOrgId && logoFacilityId
      ? `/organizations/${activeOrgId}/facilities/${logoFacilityId}/overview`
      : "/new-organization";

  return (
    <Sidebar
      className={cn(
        "hidden md:flex flex-col border-r z-10 bg-white border-[var(--sidebar-border)] transition-all duration-300",
        isCollapsed ? "w-[64px]" : "w-[280px]",
      )}
    >
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <SidebarHeader
        className={cn(
          "border-b border-[var(--sidebar-border)] h-[68px] flex flex-row items-center justify-between",
          isCollapsed ? "px-3" : "px-7",
        )}
      >
        {isCollapsed ? (
          <div className="flex items-center justify-center w-full">
            <img
              src={logoMark}
              alt="App Logo"
              className="h-8 w-8 object-contain"
            />
          </div>
        ) : (
          <Link to={logoHref} className="flex items-center">
            <img
              src={logo}
              alt="App Logo"
              className="h-17 w-45 object-contain ml-3"
            />
          </Link>
        )}
      </SidebarHeader>

      {/* ── Content ─────────────────────────────────────────────────────── */}
      <SidebarContent
        className={cn(
          "py-3 overflow-y-auto flex-1 min-h-0",
          isCollapsed ? "px-2" : "px-3",
        )}
      >
        {isCollapsed ? (
          // ── Collapsed layout — icons only ────────────────────────────────
          <div className="flex flex-col items-center gap-1">
            {visibleNavItems.map((item) => (
              <CollapsedNavItem key={item.path} {...item} />
            ))}

            {visibleNavItems.length > 0 && (
              <div className="w-full border-t border-[var(--sidebar-border)] my-2" />
            )}

            {!loading &&
              !error &&
              organizations.map((org) => (
                <OrgNavGroup
                  key={org.id}
                  org={org}
                  isCollapsed
                  defaultOpen={isPlatformUser}
                />
              ))}

            {showNoFacilitiesMessage && (
              <div
                title="No active facility. Contact your admin for details."
                className="flex items-center justify-center size-9 rounded-md text-[var(--nav-item-text-muted)]"
              >
                <PowerOff className="size-[15px]" />
              </div>
            )}

            {/* Add org — platform admins only, no tooltip */}
            {canAccess(ADD_ORG_NAV_ITEM.pageKey!, role) && (
              <>
                <div className="w-full border-t border-[var(--sidebar-border)] my-2" />
                <Link
                  to="/new-organization"
                  className="flex items-center justify-center size-9 rounded-md text-[var(--nav-item-text)] hover:bg-[#eef4fe] hover:text-[var(--deep-sky-denim)] transition-colors"
                >
                  <Plus className="size-[15px]" />
                </Link>
              </>
            )}
          </div>
        ) : (
          // ── Expanded layout ──────────────────────────────────────────────
          <SidebarMenu className="gap-0.5">
            {visibleNavItems.length > 0 && (
              <>
                {visibleNavItems.map((item) => (
                  <StaticNavLeaf key={item.path} {...item} />
                ))}
                <li className="pt-3 pb-1">
                  <hr className="border-[var(--sidebar-border)] mb-2" />
                </li>
              </>
            )}

            <li className="pb-1">
              <span className="px-3 text-[10px] font-semibold tracking-widest uppercase text-[var(--nav-section-label)]">
                Organizations
              </span>
            </li>

            {loading && (
              <li className="px-3 py-2 text-xs text-[var(--nav-item-text-muted)]">
                Loading…
              </li>
            )}
            {error && !organizations.length && (
              <li className="px-3 py-2 text-xs text-red-500">{error}</li>
            )}
            {!loading &&
              !error &&
              organizations.map((org) => (
                <OrgNavGroup
                  key={org.id}
                  org={org}
                  isCollapsed={false}
                  defaultOpen={isPlatformUser}
                />
              ))}

            {showNoFacilitiesMessage && (
              <li className="flex items-start gap-2 px-3 py-2 text-xs leading-relaxed text-[var(--nav-item-text-muted)]">
                <PowerOff className="size-[15px] shrink-0 mt-0.5" />
                <span>No active facility. Contact your admin for details.</span>
              </li>
            )}

            {/* Add Organization — hidden for org/facility roles */}
            {canAccess(ADD_ORG_NAV_ITEM.pageKey!, role) && (
              <>
                <li className="pt-2">
                  <hr className="border-[var(--sidebar-border)] mb-2" />
                </li>
                <StaticNavLeaf {...ADD_ORG_NAV_ITEM} />
              </>
            )}
          </SidebarMenu>
        )}
      </SidebarContent>

      {/* ── Footer — Collapse toggle only ───────────────────────────────── */}
      <SidebarFooter
        className={cn(
          "border-t border-[var(--sidebar-border)] p-3",
          isCollapsed ? "flex items-center justify-center" : "",
        )}
      >
        <button
          type="button"
          onClick={() => onCollapsedChange(!isCollapsed)}
          className={cn(
            "flex items-center gap-2 text-[13px] text-[var(--custom-gray-600)] font-medium hover:text-[var(--deep-sky-denim)] transition-colors cursor-pointer rounded-md px-2 py-1.5 hover:bg-[#eef4fe]",
            isCollapsed ? "justify-center w-full" : "",
          )}
        >
          {isCollapsed ? (
            <PanelLeftOpen className="size-[15px] shrink-0" />
          ) : (
            <>
              <PanelLeftClose className="size-[15px] shrink-0" />
              <span>Collapse</span>
            </>
          )}
        </button>
      </SidebarFooter>
    </Sidebar>
  );
}